import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  CircularProgress,
} from "@mui/material";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, useStripe, useElements, CardNumberElement, CardExpiryElement, CardCvcElement } from "@stripe/react-stripe-js";
import Header from "../Homepage/Components/Header";
import { Link, useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth, firestore } from "../../firebaseConfig";
import { doc, updateDoc } from "firebase/firestore";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Stripe from "./Stripe.png"

// Load Stripe
const stripePromise = loadStripe("pk_live_51OZ8VoCV8H3kriX2Erax8lq3krzzej9yXF3eaY28QhWB7WfIxYKqvqItjIbW3qkmQkn2d7tpqkD2rhUHTbTYGf9q00I2RR5WcV");
const planId = "price_1RJES2CV8H3kriX2exSA9PGW"; // Replace with your Stripe plan ID


const PaymentForm = () => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate(); // To navigate to manage subscription page
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [successMessage, setSuccessMessage] = useState(""); // Success message state

  const updatePaidUserFlag = async (uid, customerId, subscriptionId) => {
    try {
      const userRef = doc(firestore, "users", uid); // Firestore reference to the user document
      await updateDoc(userRef, {
        isPaidUserFlag: true, // Set the flag to true
        customer_id: customerId, // Add the Stripe customer ID
        subscription_id: subscriptionId, // Add the Stripe subscription ID
      });
      console.log("User document updated with subscription details.");
    } catch (error) {
      console.error("Error updating user document: ", error);
    }
  };


  const convertStripeError = (error) => {
    const errorCode = error.code || error.message;

    // Mapping raw Stripe error codes/messages to user-friendly ones
    const errorMessages = {
      "card_declined": "Your card was declined. Please check with your bank or try another card.",
      "expired_card": "Your card has expired. Please use a different card.",
      "incorrect_cvc": "The CVC code you entered is incorrect. Please try again.",
      "insufficient_funds": "Insufficient funds. Please check your balance or try a different payment method.",
      "processing_error": "There was an error processing your payment. Please try again later.",
      "invalid_number": "The card number entered is invalid. Please check and try again.",
      "other": "An error occurred with your payment. Please try again or contact support."
    };

    // If there's a specific match in the error code, return a user-friendly message
    return errorMessages[errorCode] || errorMessages["other"];
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
      } else {
        setUser(null);
      }
    });

    return () => unsubscribe();
  }, []);

  const handlePayment = async () => {
    if (!stripe || !elements) return;

    setLoading(true);
    setError(null);

    try {
      // 1. Get the payment method from the card element
      const cardElement = elements.getElement(CardNumberElement);
      const { paymentMethod, error: paymentMethodError } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
      });

      if (paymentMethodError) {
        setError(convertStripeError(paymentMethodError));
        setLoading(false);
        return;
      }
      // 2. Send payment method and other details to the backend
      const response = await fetch("https://app.stock101.org/create-subscription", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          paymentMethodId: paymentMethod.id,
          customerId: user.email, // Replace with your email state variable
          planId: planId, // Replace with your plan ID
        }),
      });

      const result = await response.json();

      if (!result.success) {
        setError(result.error || "An error occurred while creating the subscription.");
        setLoading(false);
        return;
      }

      // 3. Confirm the subscription payment
      const { client_secret, subscription_id, customer_id, next_billing_date } = result;
      const { error: confirmError } = await stripe.confirmCardPayment(client_secret);

      if (confirmError) {
        setError(convertStripeError(confirmError)); // Convert Stripe error to user-friendly message
      } else {
        // Payment was successful
        setSuccessMessage("Subscription successful! You are now a Pro user.");

        // Update Firestore with subscription details
        await updatePaidUserFlag(user.uid, customer_id, subscription_id);

        // Redirect to manage subscription page after 2 seconds (to let message display)
        setTimeout(() => {
          navigate("/ManageSubscription"); // Navigate to the subscription management page
        }, 2000);
      }
    } catch (err) {
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Header />

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "32px",
          padding: "32px",
          backgroundColor: "#f9f9f9",
          minHeight: "100vh",
        }}
      >
        {/* Pro License Section */}
        <Box
          sx={{
            width: "100%",
            maxWidth: "500px",
            backgroundColor: "#ffffff",
            borderRadius: "12px",
            boxShadow: "0 6px 15px rgba(0, 0, 0, 0.1)",
            padding: "24px",
            textAlign: "center",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: "bold",
              color: "#333",
              marginBottom: "8px",
              fontFamily: "'Work Sans', sans-serif",
            }}
          >
            Pro License
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#666",
              marginBottom: "16px",
              fontFamily: "'Work Sans', sans-serif",
            }}
          >
            $8 USD/month • Cancel anytime
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "#999",
              fontSize: "0.9rem",
              fontFamily: "'Work Sans', sans-serif",
            }}
          >
            Gain full access to advanced stock analytics
          </Typography>
        </Box>

        {/* Payment Details Form */}
        <Card
          sx={{
            width: "100%",
            maxWidth: "500px",
            borderRadius: "12px",
            boxShadow: "0 6px 15px rgba(0, 0, 0, 0.1)",
            backgroundColor: "#ffffff",
          }}
        >
          <CardContent>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 500, // Slightly lighter weight for modern appearance
                marginBottom: "16px",
                fontFamily: "'Work Sans', sans-serif",
                fontSize: "1rem", // Slightly smaller for a minimalist look
                color: "#333", // Muted dark gray for text
                textAlign: "center", // Centered alignment for a balanced look
                letterSpacing: "0.5px", // Slight letter spacing for clarity
              }}
            >
              Payment Details
            </Typography>
            <Grid container spacing={2}>
              {/* Card Number */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                    padding: "12px",
                    backgroundColor: "#fdfdfd",
                  }}
                >
                  <CardNumberElement
                    options={{
                      style: {
                        base: {
                          fontSize: "16px",
                          color: "#333",
                          "::placeholder": {
                            color: "#aaa",
                          },
                        },
                      },
                    }}
                  />
                </Box>
              </Grid>
              {/* Expiry Date */}
              <Grid item xs={6}>
                <Box
                  sx={{
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                    padding: "12px",
                    backgroundColor: "#fdfdfd",
                  }}
                >
                  <CardExpiryElement
                    options={{
                      style: {
                        base: {
                          fontSize: "16px",
                          color: "#333",
                          "::placeholder": {
                            color: "#aaa",
                          },
                        },
                      },
                    }}
                  />
                </Box>
              </Grid>
              {/* CVC */}
              <Grid item xs={6}>
                <Box
                  sx={{
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                    padding: "12px",
                    backgroundColor: "#fdfdfd",
                  }}
                >
                  <CardCvcElement
                    options={{
                      style: {
                        base: {
                          fontSize: "16px",
                          color: "#333",
                          "::placeholder": {
                            color: "#aaa",
                          },
                        },
                      },
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Pay Button */}
        <Box sx={{ display: "flex", justifyContent: "center", marginTop: "24px" }}>
          <Button
            variant="contained"
            color="primary"
            sx={{
              padding: "12px 32px",
              fontSize: "1rem",
              textTransform: "none",
              borderRadius: "20px",
            }}
            disabled={!stripe || loading}
            onClick={handlePayment}
          >
            {loading ? <CircularProgress size={24} /> : "Subscribe Now"}
          </Button>


        </Box>

        <Box
  sx={{
    marginTop: "32px",
    padding: "24px",
    borderRadius: "12px",
    backgroundColor: "#f9f9f9",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
    textAlign: "center",
  }}
>
  <Typography
    variant="h6"
    sx={{
      fontWeight: "bold",
      fontFamily: "'Work Sans', sans-serif",
      color: "#333",
      marginBottom: "12px",
      fontSize: "1rem",
    }}
  >
    Secure Payment with Stripe
  </Typography>
  <Typography
    variant="body2"
    sx={{
      color: "#666",
      fontFamily: "'Work Sans', sans-serif",
      marginBottom: "16px",
      lineHeight: 1.6,
      fontSize: "0.9rem",
    }}
  >
    At <strong>Stock101</strong>, your payment security is our top priority. 
    We use <a href="https://stripe.com" target="_blank" rel="noopener noreferrer" style={{ color: "#007bfc", textDecoration: "none" }}>Stripe</a>, a 
    <a href="https://www.pcisecuritystandards.org/about_us/" target="_blank" rel="noopener noreferrer" style={{ color: "#007bfc", textDecoration: "none" }}>
      PCI-certified
    </a> payment provider trusted globally.
  </Typography>
  <Typography
    variant="body2"
    sx={{
      color: "#666",
      fontFamily: "'Work Sans', sans-serif",
      lineHeight: 1.6,
      fontSize: "0.9rem",
      marginBottom: "16px",
    }}
  >
    Your credit card data is directly processed by Stripe over a secure SSL connection. <strong>Stock101</strong> never collects or stores sensitive payment information.
  </Typography>
  <Typography
    variant="body2"
    sx={{
      color: "#666",
      fontFamily: "'Work Sans', sans-serif",
      fontSize: "0.9rem",
      lineHeight: 1.6,
    }}
  >
    For more details, visit our <Link to="/termsofuse" style={{ color: "#007bfc", textDecoration: "none" }}>Privacy Policy</Link>.
  </Typography>

  {/* Stripe Logo */}
  <Box sx={{ marginTop: "16px" }}>
    <img
      src={Stripe} // Use your Stripe logo path
      alt="Powered by Stripe"
      style={{ width: "120px", marginTop: "8px" }}
    />
  </Box>
</Box>

{error && (
          <Box sx={{ marginTop: "16px", color: "red", textAlign: "center" }}>
            <Typography variant="body1" sx={{ fontSize: "1rem" }}>
              {error}
            </Typography>
          </Box>
        )}

        {/* Success Message */}
        {successMessage && (
  <Box sx={{ display: 'flex', alignItems: 'center', marginTop: '16px' }}>
    {/* Green Tick Icon */}
    <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: '1.2rem', marginRight: '8px' }} />
    
    {/* Success Message Text */}
    <Typography
      variant="body2"
      sx={{
        color: "#4CAF50",
        fontSize: "1rem",
        fontWeight: "500",
        letterSpacing: "0.5px",
        display: 'inline', // Keeps the text inline with the icon
      }}
    >
      {successMessage}
    </Typography>
  </Box>

  
)}
      </Box>
      
    </Box>
  );
};

const StripePayment = () => (
  <Elements stripe={stripePromise}>
    <PaymentForm />
  </Elements>
);

export default StripePayment;
